import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Activity, ChartNoAxesColumn, FileWarning, MessageCircle, ThumbsUp } from 'lucide-react';
import { toolLogo } from './AppChrome';
const GIGA_MARK = 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga-mark.png';
type Capture = {
  date: string;
  source: string;
  event: string;
  kind: 'Discussion' | 'Decision' | 'Issue' | 'Customer signal' | 'Metric';
  cited?: boolean;
};
const KIND_ICON: Record<Capture['kind'], React.ElementType> = {
  Discussion: MessageCircle,
  Decision: ThumbsUp,
  Issue: FileWarning,
  'Customer signal': Activity,
  Metric: ChartNoAxesColumn
};

/**
 * Three stages of the same workspace. The point of the section is that the
 * answer gets better without anyone writing documentation, so the only thing
 * that changes between stages is how much ordinary work has happened.
 */
const STAGES = [{
  label: 'Week 1',
  facts: 34,
  captures: [{
    date: '4 Mar',
    source: 'slack',
    event: '#product debates dashboard polish vs. setup friction',
    kind: 'Discussion' as const
  }, {
    date: '5 Mar',
    source: 'linear',
    event: 'Two workspace-import bugs filed against onboarding',
    kind: 'Issue' as const
  }],
  answer: ['I can see the debate happened in #product on 4 March, but no decision has been recorded yet.'],
  hint: 'One week in, the brain has heard the conversation but not the outcome.'
}, {
  label: 'Week 6',
  facts: 612,
  captures: [{
    date: '4 Mar',
    source: 'slack',
    event: '#product debates dashboard polish vs. setup friction',
    kind: 'Discussion' as const,
    cited: true
  }, {
    date: '6 Mar',
    source: 'granola',
    event: 'Planning call ends: ship the guided setup checklist first',
    kind: 'Decision' as const,
    cited: true
  }, {
    date: '19 Mar',
    source: 'intercom',
    event: '31 trial conversations mention unclear setup',
    kind: 'Customer signal' as const
  }],
  answer: ['The decision was made on 6 March: ship the guided setup checklist before dashboard polish.', 'It came out of the 4 March debate in #product, where setup friction was judged the bigger drag on activation.'],
  hint: 'The decision is now in, along with the argument that produced it.'
}, {
  label: 'Week 20',
  facts: 4318,
  captures: [{
    date: '4 Mar',
    source: 'slack',
    event: '#product debates dashboard polish vs. setup friction',
    kind: 'Discussion' as const,
    cited: true
  }, {
    date: '6 Mar',
    source: 'granola',
    event: 'Planning call ends: ship the guided setup checklist first',
    kind: 'Decision' as const,
    cited: true
  }, {
    date: '19 Mar',
    source: 'intercom',
    event: '31 trial conversations mention unclear setup',
    kind: 'Customer signal' as const,
    cited: true
  }, {
    date: '2 Apr',
    source: 'linear',
    event: 'Import bugs closed — larger teams unblocked',
    kind: 'Issue' as const
  }, {
    date: '28 Apr',
    source: 'snowflake',
    event: 'Activation up 14% after the checklist shipped',
    kind: 'Metric' as const,
    cited: true
  }],
  answer: ['Setup friction beat dashboard polish. The call was made on 6 March, out of the 4 March debate in #product.', 'It was the right call: 31 trial conversations in March named setup as the blocker, and activation rose 14% once the checklist shipped in April.', 'The dashboard work is still unstarted, and nobody has revisited it since.'],
  hint: 'Five months of ordinary work, and the answer now carries the argument, the evidence, and the result.'
}];
export function CompanyBrainPillar() {
  const [stage, setStage] = useState(2);
  // Plays through once so the compounding is visible without touching anything,
  // then stays wherever the visitor leaves it.
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (touched) return;
    setStage(0);
    const timers = [window.setTimeout(() => setStage(1), 2600), window.setTimeout(() => setStage(2), 5600)];
    return () => timers.forEach(t => window.clearTimeout(t));
  }, [touched]);
  const current = STAGES[stage];
  return <div data-id="element-222" data-mp="38i9jw9fjso9m">
      <div className="mx-auto max-w-3xl text-center" data-id="element-223" data-mp="38hg98xdgxem7">
        <p className="mb-4 text-[12px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-224" data-mp="38kvnjxzbijmk">
          The compounding part
        </p>
        <h2 className="font-title text-4xl font-bold leading-[1.05] tracking-tight text-[#2B211A] md:text-[56px]" data-id="element-225" data-mp="3hjz9rats7f5a">
          A company brain nobody
          <br data-id="element-226" data-mp="3mg7jg8p7mg52" />
          has to maintain.
        </h2>
        <p className="mx-auto mt-5 max-w-[54ch] text-lg leading-relaxed text-[#6B5D52]" data-id="element-227" data-mp="3ra20t5hcej0z">
          Every conversation, decision, workflow run, and connected system
          leaves something behind. Giga keeps it — so the longer your company
          works, the better its AI understands the company.
        </p>
      </div>

      <div className="mx-auto mt-12 max-w-[1080px] md:mt-16" data-id="element-228" data-mp="93notc2mwj58">
        {/* Scrubbing time is the argument. A static diagram cannot show that the
         same question gets a better answer purely because months passed. */}
        <div className="mb-6 flex flex-col items-center gap-4 sm:flex-row sm:justify-between" data-id="element-229" data-mp="1oook3tge25l7">
          <div className="flex items-center gap-1 rounded-full border border-[#E7E0D4] bg-white p-1" data-id="element-230" data-mp="1kml8n4tpz0zx">
            {STAGES.map((s, i) => <button key={s.label} type="button" onClick={() => {
            setTouched(true);
            setStage(i);
          }} aria-pressed={stage === i} className={`rounded-full px-4 py-1.5 text-[13px] font-semibold transition-colors ${stage === i ? 'bg-[#2B211A] text-white' : 'text-[#6B5D52] hover:text-[#2B211A]'}`} data-id="element-231" data-mp="2cbocijhp29c1" data-mpi={i}>
                {s.label}
              </button>)}
          </div>
          <p className="flex items-baseline gap-2 text-[#6B5D52]" data-id="element-232" data-mp="2vzncr0emty37">
            <motion.span key={current.facts} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.24,
            ease: [0.23, 1, 0.32, 1]
          }} className="font-title text-3xl font-bold tracking-tight text-[#2B211A]" data-id="element-233" data-mp="2w204246a5ksx">
              {current.facts.toLocaleString()}
            </motion.span>
            <span className="text-[14px] font-medium" data-id="element-234" data-mp="31bax7wij9j7l">things understood</span>
          </p>
        </div>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.92fr)]" data-id="element-235" data-mp="2rexu6813lt06">
          {/* Left: the work itself. No one authored any of these entries. */}
          <div className="rounded-[22px] border border-[#E7E0D4] bg-white p-5 shadow-[0_30px_80px_-48px_rgba(43,33,26,0.45)]" data-id="element-236" data-mp="2wflu5j7b1k6j">
            <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.12em] text-neutral-400" data-id="element-237" data-mp="2igm5wcnpf7aw">
              What the company did
            </p>
            <ul className="space-y-2.5" data-id="element-238" data-mp="gny2a275ehul">
              <AnimatePresence initial={false} data-id="element-239" data-mp="gksdko2flhze">
                {current.captures.map((c, i) => <motion.li key={c.date + c.event} initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.26,
                delay: i * 0.04,
                ease: [0.23, 1, 0.32, 1]
              }} className={`flex gap-3 rounded-2xl border px-3.5 py-3 transition-colors ${c.cited ? 'border-[#EA580C]/35 bg-[#FFF8F3]' : 'border-[#EFEAE1]'}`} data-id="element-240" data-mp="knoyilfg1q6i" data-mpi={i}>
                    <img src={toolLogo(c.source)} alt="" aria-hidden="true" className="mt-0.5 size-4 shrink-0 object-contain" data-id="element-241" data-mp="2jvowhc1yfiqy" />
                    <span className="min-w-0 flex-1" data-id="element-242" data-mp="2xu52f0rzt581">
                      <span className="block text-[13.5px] leading-snug text-[#2B211A]" data-id="element-243" data-mp="2xsty8zumxvge">
                        {c.event}
                      </span>
                      <span className="mt-1.5 flex items-center gap-2 text-[11.5px] text-neutral-500" data-id="element-244" data-mp="13i89129zppac">
                        <span data-id="element-245" data-mp="yjdo9usgpqnl">{c.date}</span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#F4F0E9] px-2 py-0.5 font-semibold" data-id="element-246" data-mp="dme8dz0edo3w">
                          {React.createElement(KIND_ICON[c.kind], {
                        size: 10
                      })}
                          {c.kind}
                        </span>
                      </span>
                    </span>
                  </motion.li>)}
              </AnimatePresence>
            </ul>
            <p className="mt-4 border-t border-[#EFEAE1] pt-3.5 text-[12.5px] leading-relaxed text-neutral-500" data-id="element-247" data-mp="3qu99qo5ypyml">
              Nobody wrote a wiki page. These are Slack threads, meetings,
              tickets, support conversations, and query results Giga was already
              part of.
            </p>
          </div>

          {/* Right: the same question, every time. */}
          <div className="rounded-[22px] border border-[#E7E0D4] bg-white p-5 shadow-[0_30px_80px_-48px_rgba(43,33,26,0.45)]" data-id="element-248" data-mp="1f46m6449y298">
            <div className="flex justify-end" data-id="element-249" data-mp="1k0x1gp898ttl">
              <p className="max-w-[88%] rounded-2xl bg-[#F4F0E9] px-4 py-2.5 text-[13.5px] font-medium text-[#2B211A]" data-id="element-250" data-mp="34kiw73tfsvwy">
                Why did we deprioritise the dashboard work?
              </p>
            </div>
            <AnimatePresence mode="wait" data-id="element-251" data-mp="2uu7hcm87t2ba">
              <motion.div key={stage} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -6
            }} transition={{
              duration: 0.26,
              ease: [0.23, 1, 0.32, 1]
            }} className="mt-4" data-id="element-252" data-mp="2ut4q2d6zcxuu">
                <div className="flex gap-2.5" data-id="element-253" data-mp="2bv20nxaisywi">
                  <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-[#EA580C]" data-id="element-254" data-mp="2bxxxtjejnjjw">
                    <img src={GIGA_MARK} alt="" className="size-3 [filter:brightness(0)_invert(1)]" data-id="element-255" data-mp="a4i3abfc5kjn" />
                  </span>
                  <div className="space-y-2.5 text-[13.5px] leading-relaxed text-[#3D3229]" data-id="element-256" data-mp="555irxbxspk8">
                    {current.answer.map(line => <p key={line} data-id="element-257" data-mp="1k1r9pujv9c0w">{line}</p>)}
                  </div>
                </div>
                <p className="mt-4 flex flex-wrap items-center gap-1.5 border-t border-[#EFEAE1] pt-3.5 text-[11.5px] text-neutral-500" data-id="element-258" data-mp="1f2xzqc3sg35h">
                  <span className="font-semibold text-[#2B211A]" data-id="element-259" data-mp="1tz5q5u5si0t7">
                    Assembled from
                  </span>
                  {current.captures.filter(c => c.cited).length || 1} piece
                  {(current.captures.filter(c => c.cited).length || 1) === 1 ? '' : 's'}{' '}
                  of work
                  {current.captures.filter(c => c.cited).map(c => <span key={c.date} className="inline-flex items-center gap-1 rounded-full border border-[#EFEAE1] px-2 py-0.5 font-medium" data-id="element-260" data-mp="1b35x0g12sr81">
                      <img src={toolLogo(c.source)} alt="" aria-hidden="true" className="size-2.5 object-contain" data-id="element-261" data-mp="im3cb6v2rpcq" />
                      {c.date}
                    </span>)}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        <p className="mt-6 text-center text-[15px] font-medium text-[#6B5D52]" data-id="element-262" data-mp="2p891h9x2u3nq">
          {current.hint}
        </p>
      </div>
    </div>;
}